// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。

/**
 * Phase 0.3（frontend-deep-optimization 计划）：web-vitals 运行时观测。
 * - 本地埋点：dev 控制台 + `window.__webVitals` 环形缓冲，供前后对比取数；
 * - 预留 `window.__webVitalsReport` 上报钩子，接外部监控时只改这一处。
 * - 静态导入：web-vitals 仅 ~3KB gzip 且经 main→App→api 层进入共享 chunk，
 *   动态加载并无收益（实测 chunk 归属不变），少一跳异步注册更简单；
 *   LCP/CLS 由 PerformanceObserver buffered 回捕，注册时机不受影响。
 */
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

export interface WebVitalSample {
  name: string;
  value: number;
  rating: string;
  id: string;
  at: number;
}

declare global {
  interface Window {
    __webVitals?: WebVitalSample[];
    __webVitalsReport?: (sample: WebVitalSample) => void;
  }
}

const MAX_SAMPLES = 40;

export function initWebVitals(): void {
  if (typeof window === 'undefined') return;
  window.__webVitals = [];
  const record = (metric: { name: string; value: number; rating: string; id: string }) => {
    const sample: WebVitalSample = {
      name: metric.name,
      value: Math.round(metric.value * 100) / 100,
      rating: metric.rating,
      id: metric.id,
      at: Date.now(),
    };
    const buffer = window.__webVitals;
    if (buffer) {
      buffer.push(sample);
      if (buffer.length > MAX_SAMPLES) buffer.shift();
    }
    if (import.meta.env.DEV) {
      console.info(`[web-vitals] ${sample.name} ${sample.value} (${sample.rating})`);
    }
    window.__webVitalsReport?.(sample);
  };
  onCLS(record);
  onFCP(record);
  onINP(record);
  onLCP(record);
  onTTFB(record);
}
